/**
 * Authentication API service.
 */
import apiClient from './client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  full_name?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface UserResponse {
  id: string;
  email: string;
  full_name?: string;
  preferred_name?: string;
  ministry_role?: string;
  denomination?: string;
  theological_tradition?: string;
  bible_translation?: string;
  subscription_tier: string;
  subscription_status?: string;
  user_role?: string;  // 'regular' | 'org_admin' | 'subuser'
  organization_id?: string;
  is_active: boolean;
  is_verified: boolean;
  is_superuser?: boolean;
  created_at: string;
  last_login?: string;

  // UI Preferences
  color_mode?: string;
  chat_font?: string;
  sidebar_collapsed?: boolean;
  show_tooltips?: boolean;

  // Notifications
  email_notifications?: boolean;
  weekly_digest?: boolean;
  sms_opt_in?: boolean;
  sms_consent_date?: string;

  // API Keys (boolean flags only, not actual keys)
  use_own_api_keys?: boolean;
  has_anthropic_key?: boolean;
  has_openai_key?: boolean;
  has_straico_key?: boolean;

  // Billing
  chargekeep_customer_id?: string;
  chargekeep_subscription_id?: string;
}

export interface UserStatsResponse {
  total_clipboard_items: number;
  clipboard_limit: number;
  total_conversations: number;
  conversation_limit: number;
  total_ai_requests_this_month: number;
  ai_request_limit: number;
  subscription_tier: string;
  storage_used_mb: number;
}

export interface TierInfoResponse {
  tier_code: string;
  tier_type: 'individual' | 'organization';
  display_name: string;
  description: string | null;
  max_subusers: number | null;
  features: Record<string, any> | null;
  clipboard_slots: number;
  conversations: number;
  ai_requests: number;
}

export const authApi = {
  async login(data: LoginRequest): Promise<TokenResponse> {
    const response = await apiClient.post('/api/auth/login', data);
    return response.data;
  },

  async register(data: RegisterRequest): Promise<UserResponse> {
    const response = await apiClient.post('/api/auth/register', data);
    return response.data;
  },

  async logout(): Promise<void> {
    await apiClient.post('/api/auth/logout');
  },

  async getCurrentUser(): Promise<UserResponse> {
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  },

  async getUserStats(): Promise<UserStatsResponse> {
    const response = await apiClient.get('/api/users/me/stats');
    return response.data;
  },

  async getTierInfo(): Promise<TierInfoResponse> {
    const response = await apiClient.get('/api/users/me/tier');
    return response.data;
  },
};
